import * as React from "react";
import { IClient, IClinician } from "intake_shared/src/types";
import { AclContext } from "../../../../../../contexts/AclContext";
import { TUpdateClient } from "../../../clientUiTypes";
import { getClinicianByEmail } from "../../../../../../store/clinician";
import { useToasts } from "react-toast-notifications";
import store from "../../../../../../store";
import FeeInputWithTooltip from "../FeeInputWithTooltip/FeeInputWithTooltip";
import { validateFees } from "../../../../../../utils/utils";
import { Dialog } from "../../../../bits/Dialog/Dialog";
import { Select } from "../../../../bits/Select/Select";
import Alert from "../../../../bits/Alert/Alert";
import { FormCheckbox } from "../../../../bits/FormCheckbox/FormCheckbox";
import styles from "./ClinicianFeesDialog.module.scss";

interface ClinicianFeesDialogProps {
    show: boolean;
    onHide: () => void;
    client: IClient;
    updateClient: TUpdateClient;
}

interface ClinicianWithFees {
    clinician: IClinician;
    fees: any;
    hasExistingFees: boolean;
}

export default function ClinicianFeesDialog({
    show,
    onHide,
    client,
    updateClient,
}: ClinicianFeesDialogProps) {
    const [selectedClinicianEmail, setSelectedClinicianEmail] =
        React.useState<string>("");
    const [cliniciansWithFees, setCliniciansWithFees] = React.useState<
        ClinicianWithFees[]
    >([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState(false);

    const loggedInUser =
        React.useContext(AclContext)?.authUser.user?.displayName || "Intake team";
    const { addToast } = useToasts();

    // Get unique clinicians from confirmed bookings
    const uniqueClinicians = React.useMemo(() => {
        const clinicianEmails = new Set<string>();
        client.confirmed_bookings?.forEach((booking) => {
            if (booking.clinician_id) {
                clinicianEmails.add(booking.clinician_id);
            }
        });
        client.held_bookings?.forEach((booking) => {
            if (booking.clinician_id) {
                clinicianEmails.add(booking.clinician_id);
            }
        });
        return Array.from(clinicianEmails);
    }, [client.confirmed_bookings, client.held_bookings]);

    React.useEffect(() => {
        const fetchCliniciansAndFees = async () => {
            if (!uniqueClinicians.length) return;

            setIsLoading(true);
            try {
                const cliniciansDataPromises = uniqueClinicians.map(
                    async (email) => {
                        const clinician = await getClinicianByEmail(email);
                        const existingFees = client.clinician_fees?.[email] || {};

                        if (clinician) {
                            return {
                                clinician,
                                fees: existingFees,
                                hasExistingFees:
                                    Object.keys(existingFees).length > 0,
                            };
                        } else {
                            addToast(`Clinician not found: ${email}`, {
                                appearance: "error",
                                autoDismiss: true,
                            });
                            return null;
                        }
                    }
                );

                const cliniciansDataResults = await Promise.all(
                    cliniciansDataPromises
                );

                const validCliniciansData = cliniciansDataResults.filter(
                    (data) => data !== null
                ) as ClinicianWithFees[];
                setCliniciansWithFees(validCliniciansData);
            } catch (error) {
                console.error("Error fetching clinicians:", error);
                addToast("Error fetching clinician information", {
                    appearance: "error",
                    autoDismiss: true,
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchCliniciansAndFees();
    }, [uniqueClinicians, client.clinician_fees, addToast]);

    const handleSaveClinicianFees = async () => {
        if (!selectedClinicianEmail) return;

        const selectedClinician = cliniciansWithFees.find(
            (c) => c.clinician.email === selectedClinicianEmail
        );
        if (!selectedClinician) return;

        try {
            setIsSaving(true);
            const updatedAt = new Date();
            const updatedClinicianFees = {
                ...client.clinician_fees,
                [selectedClinicianEmail]: selectedClinician.fees,
            };

            // Save to database using updateClientData
            await store.updateClientData({
                client_id: client.id,
                changes: {
                    clinician_fees: updatedClinicianFees,
                    fees_update_details: {
                        updated_by: loggedInUser,
                        updated_at: updatedAt,
                        reason_for_fee_update: `Updated fees for clinician ${selectedClinicianEmail}`,
                    },
                },
                last_updated_by: loggedInUser,
            });

            // Update local state
            updateClient("clinician_fees", updatedClinicianFees);
            updateClient("fees_update_details", {
                updated_by: loggedInUser,
                updated_at: updatedAt,
                reason_for_fee_update: `Updated fees for clinician ${selectedClinicianEmail}`,
            });
            updateClient("loaded_fee_revision", updatedAt.toISOString());

            // Show success message
            addToast(
                `Fees updated successfully for ${selectedClinician.clinician.fullname}`,
                {
                    appearance: "success",
                    autoDismiss: true,
                }
            );

            // Close modal only on success
            onHide();
        } catch (error) {
            console.error("Error saving clinician fees:", error);
            addToast("Failed to save clinician fees. Please try again.", {
                appearance: "error",
                autoDismiss: true,
            });
            // Don't close modal on error
        } finally {
            setIsSaving(false);
        }
    };

    const handleFeeChange = (field: string, value: string | boolean) => {
        if (!selectedClinicianEmail) return;

        setCliniciansWithFees((prev) =>
            prev.map((c) =>
                c.clinician.email === selectedClinicianEmail
                    ? { ...c, fees: { ...c.fees, [field]: value } }
                    : c
            )
        );
    };

    const selectedClinicianData = cliniciansWithFees.find(
        (c) => c.clinician.email === selectedClinicianEmail
    );

    const clinicianOptions = [
        { value: "", label: "Choose a clinician..." },
        ...cliniciansWithFees.map((clinicianData) => ({
            value: clinicianData.clinician.email,
            label: `${clinicianData.clinician.fullname} (${
                clinicianData.clinician.email
            })${
                clinicianData.clinician.credential
                    ? ` - ${clinicianData.clinician.credential}`
                    : ""
            }${clinicianData.hasExistingFees ? " - Has custom fees" : ""}`,
        })),
    ];

    return (
        <Dialog
            open={show}
            onClose={onHide}
            title="Set Fees Per Clinician"
            onSave={handleSaveClinicianFees}
            onCancel={onHide}
            saveText={isSaving ? "Saving..." : "Save Fees"}
            cancelText="Cancel"
            isSubmitBtnDisabled={!selectedClinicianEmail || isSaving}
        >
            <div className={styles.content}>
                <div className={styles.selectWrapper}>
                    <Select
                        label="Select Clinician"
                        value={
                            clinicianOptions.find(
                                (opt) => opt.value === selectedClinicianEmail
                            ) || null
                        }
                        onChange={(option: any) =>
                            setSelectedClinicianEmail(option?.value || "")
                        }
                        options={clinicianOptions}
                        isLongListInDialog
                    />
                </div>

                {selectedClinicianData && (
                    <div className={styles.feesCard}>
                        <Alert className={styles.alert} type="info">
                            <p className={styles.alertText}>
                                <strong>Note:</strong> These fees will override the
                                general fees for this specific clinician. Leave
                                fields empty to use general fees.
                            </p>
                            <p className={styles.alertText}>
                                <strong>Format examples:</strong> 120x7; 90, 80x5; 15
                                TBD, 120TBD, 25
                            </p>
                        </Alert>
                        <div className={styles.cardBody}>
                            <h5 className={styles.cardTitle}>
                                Fees for {selectedClinicianData.clinician.fullname}
                            </h5>

                            {/* ONN Fees Section */}
                            <h6 className={styles.sectionTitle}>ONN Fees</h6>
                            <div className={styles.feesGrid}>
                                <div className={styles.feesColumn}>
                                    <FeeInputWithTooltip
                                        label="Masters Fee"
                                        value={
                                            selectedClinicianData.fees.masters_fee ||
                                            ""
                                        }
                                        onChange={(value) =>
                                            handleFeeChange("masters_fee", value)
                                        }
                                        calcValue={validateFees(
                                            selectedClinicianData.fees.masters_fee ||
                                                "",
                                            "masters_fee"
                                        )}
                                    />

                                    <FeeInputWithTooltip
                                        label="Doctoral Fee"
                                        value={
                                            selectedClinicianData.fees.dr_fee || ""
                                        }
                                        onChange={(value) =>
                                            handleFeeChange("dr_fee", value)
                                        }
                                        calcValue={validateFees(
                                            selectedClinicianData.fees.dr_fee || "",
                                            "dr_fee"
                                        )}
                                    />

                                    <FeeInputWithTooltip
                                        label="Dr. Fee A"
                                        value={
                                            selectedClinicianData.fees.dr_fee_a || ""
                                        }
                                        onChange={(value) =>
                                            handleFeeChange("dr_fee_a", value)
                                        }
                                        calcValue={validateFees(
                                            selectedClinicianData.fees.dr_fee_a ||
                                                "",
                                            "dr_fee_a"
                                        )}
                                    />

                                    <FeeInputWithTooltip
                                        label="Dr. Fee B"
                                        value={
                                            selectedClinicianData.fees.dr_fee_b || ""
                                        }
                                        onChange={(value) =>
                                            handleFeeChange("dr_fee_b", value)
                                        }
                                        calcValue={validateFees(
                                            selectedClinicianData.fees.dr_fee_b ||
                                                "",
                                            "dr_fee_b"
                                        )}
                                    />

                                    <FeeInputWithTooltip
                                        label="Dr. Fee C"
                                        value={
                                            selectedClinicianData.fees.dr_fee_c || ""
                                        }
                                        onChange={(value) =>
                                            handleFeeChange("dr_fee_c", value)
                                        }
                                        calcValue={validateFees(
                                            selectedClinicianData.fees.dr_fee_c ||
                                                "",
                                            "dr_fee_c"
                                        )}
                                    />

                                    <FeeInputWithTooltip
                                        label="Psychiatry Fee"
                                        value={
                                            selectedClinicianData.fees
                                                .psychiatry_fee || ""
                                        }
                                        onChange={(value) =>
                                            handleFeeChange("psychiatry_fee", value)
                                        }
                                        calcValue={validateFees(
                                            selectedClinicianData.fees
                                                .psychiatry_fee || "",
                                            "psychiatry_fee"
                                        )}
                                    />

                                    <FeeInputWithTooltip
                                        label="Psych A Fee"
                                        value={
                                            selectedClinicianData.fees
                                                .psychiatry_fee_a || ""
                                        }
                                        onChange={(value) =>
                                            handleFeeChange(
                                                "psychiatry_fee_a",
                                                value
                                            )
                                        }
                                        calcValue={validateFees(
                                            selectedClinicianData.fees
                                                .psychiatry_fee_a || "",
                                            "psychiatry_fee_a"
                                        )}
                                    />

                                    <FeeInputWithTooltip
                                        label="Psych B Fee"
                                        value={
                                            selectedClinicianData.fees
                                                .psychiatry_fee_b || ""
                                        }
                                        onChange={(value) =>
                                            handleFeeChange(
                                                "psychiatry_fee_b",
                                                value
                                            )
                                        }
                                        calcValue={validateFees(
                                            selectedClinicianData.fees
                                                .psychiatry_fee_b || "",
                                            "psychiatry_fee_b"
                                        )}
                                    />

                                    <FeeInputWithTooltip
                                        label="Psych C Fee"
                                        value={
                                            selectedClinicianData.fees
                                                .psychiatry_fee_c || ""
                                        }
                                        onChange={(value) =>
                                            handleFeeChange(
                                                "psychiatry_fee_c",
                                                value
                                            )
                                        }
                                        calcValue={validateFees(
                                            selectedClinicianData.fees
                                                .psychiatry_fee_c || "",
                                            "psychiatry_fee_c"
                                        )}
                                    />

                                    <FeeInputWithTooltip
                                        label="Intern Fee"
                                        value={
                                            selectedClinicianData.fees.intern_fee ||
                                            ""
                                        }
                                        onChange={(value) =>
                                            handleFeeChange("intern_fee", value)
                                        }
                                        calcValue={validateFees(
                                            selectedClinicianData.fees.intern_fee ||
                                                "",
                                            "intern_fee"
                                        )}
                                    />

                                    <FeeInputWithTooltip
                                        label="Lic Fee"
                                        value={
                                            selectedClinicianData.fees.lic_fee || ""
                                        }
                                        onChange={(value) =>
                                            handleFeeChange("lic_fee", value)
                                        }
                                        calcValue={validateFees(
                                            selectedClinicianData.fees.lic_fee || "",
                                            "lic_fee"
                                        )}
                                    />

                                    <FeeInputWithTooltip
                                        label="LIC A Fee"
                                        value={
                                            selectedClinicianData.fees.lic_a_fee ||
                                            ""
                                        }
                                        onChange={(value) =>
                                            handleFeeChange("lic_a_fee", value)
                                        }
                                        calcValue={validateFees(
                                            selectedClinicianData.fees.lic_a_fee ||
                                                "",
                                            "lic_a_fee"
                                        )}
                                    />

                                    <FeeInputWithTooltip
                                        label="LIC B Fee"
                                        value={
                                            selectedClinicianData.fees.lic_b_fee ||
                                            ""
                                        }
                                        onChange={(value) =>
                                            handleFeeChange("lic_b_fee", value)
                                        }
                                        calcValue={validateFees(
                                            selectedClinicianData.fees.lic_b_fee ||
                                                "",
                                            "lic_b_fee"
                                        )}
                                    />

                                    <FeeInputWithTooltip
                                        label="LIC C Fee"
                                        value={
                                            selectedClinicianData.fees.lic_c_fee ||
                                            ""
                                        }
                                        onChange={(value) =>
                                            handleFeeChange("lic_c_fee", value)
                                        }
                                        calcValue={validateFees(
                                            selectedClinicianData.fees.lic_c_fee ||
                                                "",
                                            "lic_c_fee"
                                        )}
                                    />
                                </div>

                                <div className={styles.feesColumn}>
                                    <FeeInputWithTooltip
                                        label="Extern Fee"
                                        value={
                                            selectedClinicianData.fees.extern_fee ||
                                            ""
                                        }
                                        onChange={(value) =>
                                            handleFeeChange("extern_fee", value)
                                        }
                                        calcValue={validateFees(
                                            selectedClinicianData.fees.extern_fee ||
                                                "",
                                            "extern_fee"
                                        )}
                                    />

                                    <FeeInputWithTooltip
                                        label="Career Fee"
                                        value={
                                            selectedClinicianData.fees.career_fee ||
                                            ""
                                        }
                                        onChange={(value) =>
                                            handleFeeChange("career_fee", value)
                                        }
                                        calcValue={validateFees(
                                            selectedClinicianData.fees.career_fee ||
                                                "",
                                            "career_fee"
                                        )}
                                    />

                                    <FeeInputWithTooltip
                                        label="Gifted Assessment Fee"
                                        value={
                                            selectedClinicianData.fees
                                                .gifted_assessment_fee || ""
                                        }
                                        onChange={(value) =>
                                            handleFeeChange(
                                                "gifted_assessment_fee",
                                                value
                                            )
                                        }
                                        calcValue={validateFees(
                                            selectedClinicianData.fees
                                                .gifted_assessment_fee || "",
                                            "gifted_assessment_fee"
                                        )}
                                    />

                                    <FeeInputWithTooltip
                                        label="Nutrition Fee"
                                        value={
                                            selectedClinicianData.fees
                                                .nutrition_fee || ""
                                        }
                                        onChange={(value) =>
                                            handleFeeChange("nutrition_fee", value)
                                        }
                                        calcValue={validateFees(
                                            selectedClinicianData.fees
                                                .nutrition_fee || "",
                                            "nutrition_fee"
                                        )}
                                    />

                                    <FeeInputWithTooltip
                                        label="Kap Fee"
                                        value={
                                            selectedClinicianData.fees.kap_fee || ""
                                        }
                                        onChange={(value) =>
                                            handleFeeChange("kap_fee", value)
                                        }
                                        calcValue={validateFees(
                                            selectedClinicianData.fees.kap_fee || "",
                                            "kap_fee"
                                        )}
                                    />

                                    <FeeInputWithTooltip
                                        label="Short Term Evaluation & Documentation Fee"
                                        value={
                                            selectedClinicianData.fees
                                                .short_term_evaluation_documentation_fee ||
                                            ""
                                        }
                                        onChange={(value) =>
                                            handleFeeChange(
                                                "short_term_evaluation_documentation_fee",
                                                value
                                            )
                                        }
                                        calcValue={validateFees(
                                            selectedClinicianData.fees
                                                .short_term_evaluation_documentation_fee ||
                                                "",
                                            "short_term_evaluation_documentation_fee"
                                        )}
                                    />

                                    <FeeInputWithTooltip
                                        label="Testing Fee"
                                        value={
                                            selectedClinicianData.fees.testing_fee ||
                                            ""
                                        }
                                        onChange={(value) =>
                                            handleFeeChange("testing_fee", value)
                                        }
                                        calcValue={validateFees(
                                            selectedClinicianData.fees.testing_fee ||
                                                "",
                                            "testing_fee"
                                        )}
                                    />

                                    <FeeInputWithTooltip
                                        label="Intern/Extern Testing Fee"
                                        value={
                                            selectedClinicianData.fees
                                                .intern_extern_testing_fee ?? ""
                                        }
                                        onChange={(value) =>
                                            handleFeeChange(
                                                "intern_extern_testing_fee",
                                                value
                                            )
                                        }
                                        skipValidation
                                    />

                                    <div className={styles.checkboxWrapper}>
                                        <FormCheckbox
                                            label="Client is Self-Pay"
                                            checked={Boolean(
                                                selectedClinicianData.fees
                                                    .is_self_pay
                                            )}
                                            onChange={(e) =>
                                                handleFeeChange(
                                                    "is_self_pay",
                                                    e.target.checked
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* INN Fees Section */}
                            <div className={styles.divider}></div>
                            <h6 className={styles.sectionTitle}>INN Fees</h6>
                            <div className={styles.feesGrid}>
                                <div className={styles.feesColumn}>
                                    <FeeInputWithTooltip
                                        label="Masters Fee"
                                        value={
                                            selectedClinicianData.fees
                                                .inn_masters_fee || ""
                                        }
                                        onChange={(value) =>
                                            handleFeeChange("inn_masters_fee", value)
                                        }
                                        calcValue={validateFees(
                                            selectedClinicianData.fees
                                                .inn_masters_fee || "",
                                            "inn_masters_fee",
                                            "INN FEES"
                                        )}
                                    />
                                </div>
                                <div className={styles.feesColumn}>
                                    <FeeInputWithTooltip
                                        label="Nutrition Fee"
                                        value={
                                            selectedClinicianData.fees
                                                .inn_nutrition_fee || ""
                                        }
                                        onChange={(value) =>
                                            handleFeeChange(
                                                "inn_nutrition_fee",
                                                value
                                            )
                                        }
                                        calcValue={validateFees(
                                            selectedClinicianData.fees
                                                .inn_nutrition_fee || "",
                                            "inn_nutrition_fee",
                                            "INN FEES"
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {!selectedClinicianEmail && (
                    <div className={styles.emptyState}>
                        <p className={styles.emptyStateText}>
                            Please select a clinician to set their specific fees.
                        </p>
                    </div>
                )}

                {isLoading && (
                    <div className={styles.loadingState}>
                        <p className={styles.loadingText}>
                            Loading clinician information...
                        </p>
                        <div className={styles.loadingSubtext}>
                            Fetching details for {uniqueClinicians.length}{" "}
                            clinician(s)...
                        </div>
                    </div>
                )}
            </div>
        </Dialog>
    );
}
